import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Button,
  RegionSelector,
  getAgeRatingRegionLabel,
  toast,
} from '@ludolio/shared-ui';
import { OrgNoticeSettings } from '@/components/OrgNoticeSettings';
import { getApiErrorMessage } from '@/lib/api-error';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import type { OrgNotice } from '@/types';

export const Route = createFileRoute('/_authenticated/organization')({
  component: OrganizationPage,
});

function OrganizationPage() {
  const user = useAuthStore((s) => s.user);
  const orgId = user?.orgId;

  const [orgName, setOrgName] = useState('');
  const [region, setRegion] = useState<string | null>(null);
  const [savedRegion, setSavedRegion] = useState<string | null>(null);
  const [notice, setNotice] = useState<OrgNotice | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRegion, setSavingRegion] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([apiClient.getOrganization(orgId), apiClient.getOrgNotice(orgId)])
      .then(([orgRes, noticeRes]) => {
        if (cancelled) return;
        if (orgRes.success && orgRes.data) {
          setOrgName(orgRes.data.name ?? '');
          const nextRegion = orgRes.data.region ?? null;
          setRegion(nextRegion);
          setSavedRegion(nextRegion);
        }
        if (noticeRes.success) setNotice(noticeRes.data ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(getApiErrorMessage(err, 'Failed to load organization settings'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const handleSaveRegion = async () => {
    if (!orgId) return;
    setError('');
    setSavingRegion(true);
    try {
      const res = await apiClient.updateOrganization(orgId, { region });
      if (res.success) {
        const nextRegion = res.data?.region ?? region;
        setRegion(nextRegion);
        setSavedRegion(nextRegion);
        toast.success('Region updated');
      } else {
        setError(res.error || res.message || 'Failed to update region');
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update region'));
    } finally {
      setSavingRegion(false);
    }
  };

  const handleUpload = async (file: File, title: string) => {
    if (!orgId) return;
    setError('');
    setSaving(true);
    try {
      const res = await apiClient.uploadOrgNotice(orgId, title, file);
      if (res.success && res.data) {
        setNotice(res.data);
        toast.success(notice ? 'Notice updated' : 'Notice uploaded');
      } else {
        setError(res.error || res.message || 'Failed to upload notice');
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to upload notice'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLink = async (url: string, title: string) => {
    if (!orgId) return;
    setError('');
    setSaving(true);
    try {
      const res = await apiClient.saveOrgNoticeLink(orgId, title, url);
      if (res.success && res.data) {
        setNotice(res.data);
        toast.success('Notice updated');
      } else {
        setError(res.error || res.message || 'Failed to save notice link');
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to save notice link'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!orgId) return;
    setError('');
    setSaving(true);
    try {
      const res = await apiClient.deleteOrgNotice(orgId);
      if (res.success) {
        setNotice(null);
        toast.success('Notice removed');
      } else {
        setError(res.error || res.message || 'Failed to remove notice');
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to remove notice'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Loading organization settings…</p>
      </div>
    );
  }

  const regionDirty = region !== savedRegion;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Organization</h1>
        <p className="text-sm text-muted-foreground">{orgName}</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <div>
          <h2 className="text-base font-semibold">Region for age rating display</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Preferred region for age rating display priority.
          </p>
        </div>
        <RegionSelector value={region} onChange={setRegion} disabled={savingRegion} />
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Current: {getAgeRatingRegionLabel(savedRegion) || 'Not set'}
          </p>
          <Button
            type="button"
            size="sm"
            onClick={handleSaveRegion}
            disabled={!regionDirty || savingRegion}
          >
            {savingRegion ? 'Saving…' : 'Save region'}
          </Button>
        </div>
      </div>

      <OrgNoticeSettings
        orgName={orgName || 'your organization'}
        notice={notice}
        saving={saving}
        error={error}
        onUpload={handleUpload}
        onSaveLink={handleSaveLink}
        onRemove={handleRemove}
      />
    </div>
  );
}
